import React, { useCallback, useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { Button, ButtonGroup, Input } from 'reactstrap';
import { CSS } from '@dnd-kit/utilities';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable } from '@dnd-kit/sortable';

import Icon from '../components/Icon';
import { Actions } from './checklist-editor-reducer';

import { useAutosaveChanges } from './utils';
import { CheckboxIcon, DropdownIcon, SortIcon, TextIcon, ToggleIcon } from './questions/icons';
import { DeleteButton } from './DeleteButton';
import { EditableQuestion } from './EditableQuestion';
import { ChecklistEditorContext, ChecklistEditorDispatchContext } from './checklist-editor-context';
import { QuestionType } from './questions';

const dndModifiers = [ restrictToVerticalAxis ];

export function EditableGroup({
    groupId,
    groupIndex,
}) {
    const {
        setNodeRef,
        setActivatorNodeRef,
        listeners,
        attributes,
        transform,
        transition,
    } = useSortable({
        id: groupId,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const dispatch = useContextSelector(ChecklistEditorDispatchContext, state => state);
    const group = useContextSelector(ChecklistEditorContext, state => state.groupsById[groupId]);
    const questionOrder = useContextSelector(ChecklistEditorContext, state => state.questionOrderByGroupId[groupId]);

    const [ isDirty, setIsDirty ] = useState(false);
    const [ name, setName ] = useState(() => group.name);

    const showEmptyStateMessage = questionOrder.length < 1;

    const saveUncommittedChanges = useCallback(() => {
        if (!isDirty) {
            return;
        }

        const nextName = name.trim();
        if (nextName !== name) {
            setName(nextName);
        }

        dispatch({
            type: Actions.UPDATE_GROUP,
            payload: {
                groupId,
                name: nextName,
            },
        });

        setIsDirty(false);
    }, [ isDirty, dispatch, name, groupId ]);

    useAutosaveChanges(isDirty, saveUncommittedChanges);

    const renameGroup = (e) => {
        const value = e.target?.value ?? '';
        if (name === value) {
            return;
        }

        setName(value);
        setIsDirty(true);
    };

    const createQuestion = (type) => dispatch({
        type: Actions.CREATE_QUESTION,
        payload: {
            type,
            groupId,
        },
    });

    const moveGroupUp = () => dispatch({
        type: Actions.MOVE_GROUP,
        payload: {
            from: groupIndex,
            to: groupIndex - 1,
        },
    });

    const moveGroupDown = () => dispatch({
        type: Actions.MOVE_GROUP,
        payload: {
            from: groupIndex,
            to: groupIndex + 1,
        },
    });

    const deleteGroup = () => dispatch({
        type: Actions.DELETE_GROUP,
        payload: {
            groupId: groupId,
        },
    });

    const sortQuestions = useCallback(({
        active,
        over,
    }) => {
        if (!over || active.id === over.id) return;

        const from = questionOrder.indexOf(active.id);
        const to = questionOrder.indexOf(over.id);

        dispatch({
            type: Actions.SORT_QUESTIONS,
            payload: {
                groupId,
                from,
                to,
            },
        });
    }, [ questionOrder, groupId ]);

    const renderedQuestionList = useMemo(() => questionOrder.map((questionId, questionIndex) => (
        <EditableQuestion
            key={ `question-${ questionId }` }
            groupId={ groupId }
            questionId={ questionId }
            questionIndex={ questionIndex }
        />
    )), [ questionOrder, groupId ]);

    return (<div
        className={ 'checklist-editor__panel checklist-editor__panel--group' }
        ref={ setNodeRef }
        style={ style }
    >
        <div
            className={ 'checklist-editor__sort-handle' }
            ref={ setActivatorNodeRef }
            { ...attributes }
            { ...listeners }
        >
            <SortIcon />
        </div>

        <div className={ 'checklist-editor__panel-head' }>
            <div className={ 'checklist-editor__panel-heading' }>
                <Input
                    type={ 'text' }
                    value={ name }
                    placeholder={ 'Enter group name' }
                    onChange={ renameGroup }
                    onBlur={ saveUncommittedChanges }
                />
            </div>
            <div className={ 'checklist-editor__actions' }>
                <ButtonGroup>
                    <Button
                        outline
                        size={ 'sm' }
                        onClick={ moveGroupUp }
                    >
                        <Icon icon="arrow-up" />
                    </Button>
                    <Button
                        outline
                        size={ 'sm' }
                        onClick={ moveGroupDown }
                    >
                        <Icon icon="arrow-down" />
                    </Button>

                </ButtonGroup>
                <DeleteButton onConfirm={ deleteGroup } />
            </div>
        </div>

        <div className={ 'checklist-editor__panel-body' }>
            { showEmptyStateMessage &&
                <p className={ 'checklist-editor__empty-message' }>This groups has no questions, add one to get started.</p> }

            <DndContext
                onDragEnd={ sortQuestions }
                modifiers={ dndModifiers }
                collisionDetection={ closestCenter }
            >
                <SortableContext items={ questionOrder }>
                    { renderedQuestionList }
                </SortableContext>
            </DndContext>
        </div>

        <div className={ 'checklist-editor__panel-foot' }>
            <div className={ 'checklist-editor__actions checklist-editor__actions--footer' }>
                <ButtonGroup>
                    <Button
                        outline
                        color={ 'primary' }
                        size={ 'md' }
                        onClick={ () => createQuestion(QuestionType.TEXT) }
                    >
                        <TextIcon />
                        &nbsp;
                        Text
                    </Button>
                    <Button
                        outline
                        color={ 'primary' }
                        size={ 'md' }
                        onClick={ () => createQuestion(QuestionType.MULTIPLE_CHOICE) }
                    >
                        <CheckboxIcon checked />
                        &nbsp;
                        Checkboxes
                    </Button>
                    <Button
                        outline
                        color={ 'primary' }
                        size={ 'md' }
                        onClick={ () => createQuestion(QuestionType.DROPDOWN) }
                    >
                        <DropdownIcon />
                        &nbsp;
                        Dropdown
                    </Button>
                    <Button
                        outline
                        color={ 'primary' }
                        size={ 'md' }
                        onClick={ () => createQuestion(QuestionType.TOGGLE) }
                    >
                        <ToggleIcon checked />
                        &nbsp;
                        Toggle
                    </Button>
                    {/* NOTE: Radio buttons aren't supported yet :)
                    <Button
                        outline
                        color={ 'primary' }
                        size={ 'md' }
                        onClick={ () => createQuestion(QuestionType.EXCLUSIVE_CHOICE) }
                    >
                        <RadioButtonIcon checked />
                        Radio Buttons
                    </Button>
                    */ }
                </ButtonGroup>
            </div>
        </div>
    </div>);
}
